import { TERRAIN } from '../core/SimConfig.js';
import { runWhenIdle } from '../utils.js';

/**
 * Splat map for procedural vegetation scattering. The texture covers the full
 * terrain grid extents (cols·tileSize × rows·tileSize, centred on
 * TERRAIN.origin) and is sampled in world XZ space.
 *
 * Decoded to raw ImageData on the CPU at load time so density queries are
 * cheap array lookups — identical pattern to TerrainTextureCache for
 * heightmaps. RGB channels are interpreted independently: each vegetation
 * asset declares which channel to read.
 *
 * UV convention matches the terrain heightmap (`Terrain._displaceVertices`):
 *   - image row 0       → north edge (worldZ = gridMinZ)
 *   - image row H-1     → south edge (worldZ = gridMaxZ)
 *   - image column 0    → west edge  (worldX = gridMinX)
 *   - image column W-1  → east edge  (worldX = gridMaxX)
 */
export default class VegetationSplatMap {
  constructor() {
    /** @type {ImageData | null} */
    this._image = null;

    const { cols, rows, tileSize, origin } = TERRAIN;
    const [ox, oz] = origin;
    this._gridMinX = ox - (cols * tileSize) / 2;
    this._gridMinZ = oz - (rows * tileSize) / 2;
    this._gridW = cols * tileSize;
    this._gridH = rows * tileSize;
  }

  /** Returns true once the splat texture has been decoded. */
  get ready() { return this._image !== null; }

  /**
   * Async load + canvas decode → ImageData. Resolves with `true` on success,
   * `false` on failure (caller can keep the system disabled).
   */
  async load(url) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        // Defer the canvas decode to idle time so the one-shot splat-map read
        // (a large getImageData) doesn't block a frame during load-in.
        runWhenIdle(() => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            ctx.drawImage(img, 0, 0);
            this._image = ctx.getImageData(0, 0, canvas.width, canvas.height);
            resolve(true);
          } catch (err) {
            console.warn(`VegetationSplatMap: decode failed for ${url}`, err);
            resolve(false);
          }
        });
      };
      img.onerror = () => {
        console.warn(`VegetationSplatMap: failed to load ${url}`);
        resolve(false);
      };
      img.src = url;
    });
  }

  /**
   * Bilinear sample of one RGB channel at a world-XZ position.
   *
   * @param {number} worldX
   * @param {number} worldZ
   * @param {0|1|2} channelOffset — byte offset into the RGBA pixel (R=0, G=1, B=2)
   * @returns {number} channel value in [0, 1], or 0 if outside the grid / not loaded
   */
  sample(worldX, worldZ, channelOffset) {
    const img = this._image;
    if (!img) return 0;

    // World → normalized UV in [0, 1] across the terrain grid
    const u = (worldX - this._gridMinX) / this._gridW;
    const v = (worldZ - this._gridMinZ) / this._gridH;
    if (u < 0 || u > 1 || v < 0 || v > 1) return 0;

    const { width, height, data } = img;
    const fx = u * (width - 1);
    const fy = v * (height - 1);

    const x0 = Math.floor(fx);
    const y0 = Math.floor(fy);
    const x1 = Math.min(width - 1,  x0 + 1);
    const y1 = Math.min(height - 1, y0 + 1);
    const sx = fx - x0;
    const sy = fy - y0;

    const c00 = data[(y0 * width + x0) * 4 + channelOffset];
    const c10 = data[(y0 * width + x1) * 4 + channelOffset];
    const c01 = data[(y1 * width + x0) * 4 + channelOffset];
    const c11 = data[(y1 * width + x1) * 4 + channelOffset];

    const v0 = c00 * (1 - sx) + c10 * sx;
    const v1 = c01 * (1 - sx) + c11 * sx;
    return (v0 * (1 - sy) + v1 * sy) / 255;
  }

  dispose() {
    this._image = null;
  }
}

// Channel name → RGBA byte offset.
export const CHANNEL_OFFSET = Object.freeze({ r: 0, g: 1, b: 2 });
